import React, { useState, useEffect } from "react";

import { Sidebar } from "primereact/sidebar";

import { BiEdit } from "react-icons/bi";
import TeamInfoSidebar from "./TeamInfoSidebar";
import CustomSearch from "../../components/common/CustomSearch";
import { getIcons } from "../../components/Constant/Constant";
import TeamFormModal from "./TeamFormModal";


import TeamService from "../Services/TeamService";
import CustomHeading from "../../components/common/CustomHeading";
import CustomButton from "../../components/common/CustomButton";
import CustomTable from "../../components/common/CustomTable";

import AdminAppBar from "../../components/adminComponents/AdminAppBar";
import { userUid } from "../../utils/storage";
import { ToastContainer } from "react-toastify";
import { showErrorToast, showInfoToast, showSuccessToast, showWarningToast } from "../../components/Constant/Notification";


const TeamList = () => {
 
  const [teamName, setTeamName] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [visibleForm, setVisibleForm] = useState(false);
  const [teamInfo, setTeamInfo] = useState({});
  const [teamCount, setTeamCount] = useState(0);
  const [teamList, setTeamList] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageToken, setPageToken] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageTokens, setPageTokens] = useState([null]);
  const [cache] = useState(() => new Map());
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Cache key generator
  const getCacheKey = (page, search) => `${page}_${search}`;

  // Fetch team list
  const getTeamList = async ({
    search = "",
    page = 1,
    direction = "",
    pageToken: customToken = null,
  } = {}) => {
    const cacheKey = getCacheKey(page, search);
    const tokenForPage = customToken ?? pageTokens[page - 1] ?? null;

    if (cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      setTeamList(cachedData.teams);
      setTeamCount(cachedData.maxCount);
      setHasMore(cachedData.hasMore);
      setNextPageToken(cachedData.nextPageToken);
      setPrevPageToken(cachedData.prevPageToken);
      setCurrentPage(page);
      setPageToken(tokenForPage);
      console.log("Data loaded from cache for page:", page, "cacheKey:", cacheKey);
      return;
    }

    try {
      setLoading(true);

      const body = {
        limit: pageSize,
        searchKey: search.trim() || null,
        startAfter: direction === "next" ? tokenForPage : null,
        prev: direction === "prev" ? tokenForPage : null,
        direction,
      };

      const response = await TeamService.getAllTeams(body);
      const teams = response.data.teams || [];
      const maxCount = response.data.totalCount || 0;
      const nextToken = response.data.pagination?.lastDocId || null;
      const prevToken = response.data.pagination?.firstDocID || null;
      const hasMore = response.data.pagination?.hasMore || false;

      const calculatedHasMore = page * pageSize < maxCount;

      const cacheData = {
        teams,
        maxCount,
        hasMore: hasMore || calculatedHasMore,
        nextPageToken: nextToken,
        prevPageToken: prevToken,
      };

      cache.set(cacheKey, cacheData);
      console.log("Data cached for page:", page, "cacheKey:", cacheKey);

      if (direction === "next" && nextToken && calculatedHasMore) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length === page) {
            newTokens.push(nextToken);
          }
          return newTokens;
        });
      } else if (direction === "previous" && prevToken && page > 1) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length >= page && newTokens[page - 1] !== prevToken) {
            newTokens[page - 1] = prevToken;
          }
          return newTokens;
        });
      }

      setTeamList(teams);
      setTeamCount(maxCount);
      setHasMore(hasMore);
      setNextPageToken(nextToken);
      setPrevPageToken(prevToken);
      setPageToken(direction === "next" ? nextToken : prevToken);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching team list:", error);
  
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getTeamList({ search: searchTerm, page: 1 });
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle pagination
  const handlePageChange = (token, direction) => {
    console.log("Page change requested:", { token, direction, currentPage });

    if (direction === "next" && hasMore) {
      const newPage = currentPage + 1;
      console.log("Moving to next page:", newPage, "with token:", nextPageToken);
      getTeamList({
        search: searchTerm,
        pageToken: nextPageToken,
        page: newPage,
        direction: "next",
      });
    } else if (direction === "previous" && currentPage > 1) {
      const newPage = currentPage - 1;
      const prevToken = pageTokens[newPage - 1] || prevPageToken;
      console.log("Moving to previous page:", newPage, "with token:", prevToken);
      getTeamList({
        search: searchTerm,
        pageToken: prevToken,
        page: newPage,
        direction: "prev",
      });
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    console.log("Page size changed to:", newPageSize);
    setPageSize(newPageSize);
  };

  // Refresh data
  const refreshData = () => {
    cache.clear();
    getTeamList({
      search: searchTerm,
      page: currentPage,
      pageToken: pageTokens[currentPage - 1] || null,
    });
  };

  // Handle team creation
  const handleSubmit = async () => {
    if (!teamName || teamName.trim() === "") {
      showErrorToast("Please enter a team name");
      return;
    }
    if (!selectedDepartment) {
      showErrorToast("Please select a department");
      return;
    }

    try {
      setLoading(true);
      const teamData = { name: teamName.trim() , department: selectedDepartment.label , createdById : userUid };
      const response = await TeamService.createTeam(teamData);
      if (response.data) {
        setIsOpen(false);
        setTeamName("");
        if (response.data?.duplicate) {
          showWarningToast(response.data?.message);
        } else {
          showSuccessToast(response?.data?.message);
        }
        refreshData();
      }
      setSelectedDepartment(null);

    } catch (error) {
      console.log(error);
      showErrorToast(error?.response?.data?.message || "Failed to create team");
      
    } finally {
      setLoading(false);
    }
  };



  // Handle team update
  const handleUpdate = async (teamId, teamName) => {
    if (!teamName || teamName.trim() === "") {
      showErrorToast("Please enter a team name");
      return;
    }

    const previousTeamName = teamInfo.name;
    if (previousTeamName === teamName.trim()) {
      setIsOpen(false);
      showInfoToast("No changes made to update");
      return;
    }
    setLoading(true);
    try {
      const teamData = { name: teamName.toLowerCase().trim() , department : teamInfo?.departmentName || selectedDepartment?.label || "", modifiedById : userUid };
      const response = await TeamService.updateTeam(teamId, teamData);
      if (response.data) {
        setIsOpen(false);
        setTeamName("");
        setSelectedDepartment(null);
        showSuccessToast(response?.data?.message);
        refreshData();
        setVisibleForm(false);
      }
    } catch (error) {
      console.log(error);
      showErrorToast(error?.response?.data?.message || "Failed to fetch team list");
    } finally {
      setLoading(false);
    }
  };

  // Effect for initial data fetch and page size changes
  useEffect(() => {
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getTeamList({ search: searchTerm, page: 1 });
  }, [pageSize]);

  // Handle edit click
  const handleEditClick = async (record) => {
    try {
      const response = await TeamService.getByIdTeam(record.id);
     const teamData = response.data;
      setTeamInfo(teamData)
      setTeamName(teamData?.name || "");
      setVisibleForm(true);
    }catch (error) {
      console.log(error);
    }
   
  };

  // Handle create click
  const handleCreateClick = () => {
    setIsOpen(true);
  };

  // Table columns
  const columns = [
    {
      key: "teamName",
      title: "Team",
      render: (text) => (
        <div
          title={text.replace(/\b\w/g, (char) => char.toUpperCase())}
          className="text-blue-500 truncate cursor-pointer text-center"
        >
          {text.replace(/\b\w/g, (char) => char.toUpperCase())}
        </div>
      ),
    },
    {
      key: "departmentName",
      title: "Department",
      render: (text) => <div className="truncate">{text}</div>,
    },
    {
      key: "teamMembersCount",
      title: "Members Count",
      render: (text) => <div className="truncate">{text}</div>,
    },
   
    {
      key: "Action",
      title: "Action",
      render: (_, record) => (
        <div
          onClick={() => handleEditClick(record)}
          className="flex justify-center items-center h-full cursor-pointer"
        >
          <BiEdit className="text-blue-500 text-lg" />
        </div>
      ),
    },
  ];

  return (
    <div className="bg-appGrayBackground pb-7">
        <ToastContainer/>
      <AdminAppBar isMenu={false} />
   
      <div className="pt-28 px-6">
        <div className="flex items-center justify-between">
          <CustomHeading
            title={
              <>
                Teams <span className="text-customGreen">({teamCount})</span>
              </>
            }
            isIcon={true}
          />
          <div className="flex items-center gap-4">
            <CustomSearch
              placeholder={"Search Teams"}
              searchTerm={searchTerm}
              handleSearchSubmit={handleSearchSubmit}
              handleSearchChange={handleSearchChange}
            />
            <CustomButton label={"Create Team"} onClick={handleCreateClick} />
          </div>
        </div>

        <div className="w-full mt-[50px]">
          <CustomTable
            columns={columns}
            data={teamList}
            isPagination={true}
            pageSize={pageSize}
            imageNotFound={getIcons().teamEmpty}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            nextId={nextPageToken}
            prevId={prevPageToken}
            hasMore={hasMore}
            loading={loading}
          />
        </div>

        <TeamFormModal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setTeamName("");
            setSelectedDepartment(null);
          }}
          handleSubmit={handleSubmit}
          setTeamName={setTeamName}
          setSelectedDepartment={setSelectedDepartment}
          selectedDepartment={selectedDepartment}
          teamName={teamName}
          loading={loading}
        />

        <Sidebar
          header={
            <div className="flex items-center justify-between w-full">
              <span className="font-semibold text-lg text-customGreen">
                {"Team Information"}
              </span>
            </div>
          }
          visible={visibleForm}
          onHide={() => {
            setVisibleForm(false);
            setTeamName("");
          }}
          style={{ width: "500px" }}
          position="right"
          className="p-sidebar-md"
        >
          <TeamInfoSidebar
            teamInfo={teamInfo}
            setTeamName={setTeamName}
            teamName={teamName}
            loading={loading}
            handleUpdate={handleUpdate}
          />
        </Sidebar>
      </div>
    </div>
  );
};

export default TeamList;